import React from 'react'
import { Dialog } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useTranslation } from 'react-i18next'
import { DetailButton } from '../common/detail'
import '../common/detail/detail.css'

export type ConfirmDialogTone = 'default' | 'danger' | 'warn'

export type ConfirmDialogDetail = {
  label: string
  value: React.ReactNode
}

interface ConfirmDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  message: string
  title?: string
  confirmText?: string
  cancelText?: string
  tone?: ConfirmDialogTone
  loading?: boolean
  /** Identifying fields shown before confirm (name, id, status, …) */
  details?: ConfirmDialogDetail[]
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  message,
  title = 'Confirm Action',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  tone = 'default',
  loading = false,
  details,
}) => {
  const { t } = useTranslation()
  const iconClass =
    tone === 'danger'
      ? 'incyia-modal-icon incyia-modal-icon--danger'
      : tone === 'warn'
        ? 'incyia-modal-icon incyia-modal-icon--warn'
        : 'incyia-modal-icon'

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ className: 'incyia-modal-paper' }}
    >
      <div className="incyia-modal-head">
        <div className={iconClass} aria-hidden>
          {tone === 'danger' ? (
            <DeleteOutlineRoundedIcon sx={{ fontSize: 22 }} />
          ) : tone === 'warn' ? (
            <WarningAmberRoundedIcon sx={{ fontSize: 22 }} />
          ) : (
            <InfoOutlinedIcon sx={{ fontSize: 22 }} />
          )}
        </div>
        <div>
          <h3>{title}</h3>
          <p>{message}</p>
        </div>
        <button
          type="button"
          className="incyia-modal-x"
          onClick={onClose}
          aria-label={t('cancel', { defaultValue: 'Close' })}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </button>
      </div>

      {details && details.length > 0 ? (
        <div className="incyia-modal-body">
          <dl className="incyia-confirm-details">
            {details.map((d) => (
              <div key={d.label} className="incyia-confirm-detail">
                <dt>{d.label}</dt>
                <dd>{d.value || '—'}</dd>
              </div>
            ))}
          </dl>
        </div>
      ) : null}

      <div className="incyia-modal-foot">
        <DetailButton variant="ghost" onClick={onClose} disabled={loading}>
          {cancelText}
        </DetailButton>
        <DetailButton
          variant={tone === 'danger' ? 'danger' : 'primary'}
          onClick={onConfirm}
          disabled={loading}
        >
          {confirmText}
        </DetailButton>
      </div>
    </Dialog>
  )
}

export default ConfirmDialog
